export type QueryHandlers = {
  handleOnSuccess?: () => void
  handleOnError?: () => void
}

export const fetchWithThrow = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  const res = await fetch(input, { ...init, credentials: 'include' })
  if (!res.ok) {
    const contentType = res.headers.get('Content-Type')

    if (contentType && contentType.includes('application/json')) {
      const json = await res.json()
      throw json
    }

    const text = await res.text().catch(() => '')
    throw new Error(text || `${res.status} ${res.statusText}`)
  }

  return res
}

// Preserve string-literal unions (e.g. enums like ProductCategory) so callers passing the
// result straight into typed query params keep their narrow type; everything else (numbers,
// booleans, objects) is serialized to a plain string.
export const stringifyValues = <T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]?: NonNullable<T[K]> extends string ? NonNullable<T[K]> : string } =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value != null && typeof value === 'object') {
        try {
          return [key, JSON.stringify(value)]
        } catch {
          return [key, undefined]
        }
      }
      return [key, value != null ? String(value) : undefined]
    }),
  ) as { [K in keyof T]?: NonNullable<T[K]> extends string ? NonNullable<T[K]> : string }
